/*
PURPOSE: Recover a page whose lazy-loaded code fails to load by reloading it once.
USE CASES:
- The router's root error element and PublicPageErrorBoundary call this with whatever they caught.
  A tab loaded from a previous build can request a chunk that build no longer serves; a reload
  fetches the current HTML and its chunks.
DOMAIN BOUNDARIES:
- Error classification + the reload only. Callers keep their own fallback UI for when it declines.
- Missing code only. A bundle calling an API route the backend no longer has is recovered by the
  separate stale-client guard in api/api.ts.
ASSUMPTIONS:
- Webpack names a failed JS chunk `ChunkLoadError`; mini-css-extract tags a failed CSS chunk with
  code `CSS_CHUNK_LOAD_FAILED`.
DOCUMENTATION LINKS: ai_docs/development/environment_setup.md#what-a-deploy-does-to-a-visitor-mid-session
*/

const LAST_RELOAD_KEY = 'coral:chunkReloadAt';
// A failure this soon after our own reload means the reload did not fix it, so stop rather than
// loop. Deploys are far further apart than this, so a long-lived tab still recovers from each one.
const RELOAD_WINDOW_MS = 5 * 60_000;

// Further failures while this document is already reloading are the same failure, not evidence
// that the reload did not work.
let reloadRequested = false;

export function isChunkLoadError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  return error.name === 'ChunkLoadError' || (error as { code?: unknown }).code === 'CSS_CHUNK_LOAD_FAILED';
}

/** Reloads the page and returns true when `error` is a chunk-load failure the guard allows. */
export function recoverFromChunkLoadError(error: unknown): boolean {
  if (!isChunkLoadError(error)) return false;
  if (reloadRequested) return true;
  const now = Date.now();
  try {
    const lastReloadAt = Number(window.sessionStorage.getItem(LAST_RELOAD_KEY));
    if (now - lastReloadAt < RELOAD_WINDOW_MS) return false;
    window.sessionStorage.setItem(LAST_RELOAD_KEY, String(now));
  } catch {
    // Without a record of the attempt there is no loop guard, so do not reload.
    return false;
  }
  reloadRequested = true;
  window.location.reload();
  return true;
}
